import { rpc } from '@/api/rpc'
import { safeName, uploadToStorage } from '@/lib/files'
import { db } from '@/offline/db'
import { enqueue, flushOutbox, registerOutboxHandler } from '@/offline/outbox'
import { requestBackgroundSync } from '@/offline/sync'

type AnswerInfo = {
  reluxId: string
  userId: string
  message: string
  fileIds: string[]
  categories: Record<string, string>
}
type Outcome = { projectId: string; stage: string; details: Record<string, unknown> }

async function queue(type: string, label: string, userId: string, payload: unknown) {
  await enqueue({ type, label, userId, payload })
  void requestBackgroundSync()
  void flushOutbox({ userId })
}

/** Reply to Cam's "Info needed" (with optional extra files). Works offline. */
export function queueAnswerInfo(a: AnswerInfo, projectName: string) {
  return queue('relux.answer_info', `Reply to the designer: ${projectName}`, a.userId, a)
}

/** Record Quoted / Won / Lost / On hold. Works offline. */
export function queueOutcome(o: Outcome, userId: string, projectName: string) {
  return queue('project.outcome', `Outcome for ${projectName}`, userId, o)
}

export function registerActionHandlers() {
  registerOutboxHandler('relux.answer_info', async (item) => {
    const a = item.payload as AnswerInfo
    const files = []
    for (const id of a.fileIds) {
      const local = await db.files.get(id)
      if (!local) continue
      const path =
        local.uploadedPath ?? `uploads/${a.userId}/${a.reluxId}/${local.id}-${safeName(local.name)}`
      if (!local.uploadedPath) {
        await uploadToStorage(path, local.blob, local.type)
        await db.files.update(local.id, { uploadedPath: path })
      }
      files.push({
        client_uuid: local.id,
        storage_path: path,
        original_name: local.name,
        mime: local.type,
        size: local.size,
        category: a.categories[id] ?? (local.type.startsWith('image/') ? 'Photo' : 'Other'),
      })
    }
    try {
      await rpc('answer_info_needed', { rid: a.reluxId, message: a.message, files })
    } catch (e) {
      // A resend after a lost response: the first attempt already reached the designer.
      const alreadyAnswered = /has not asked for anything/i.test((e as Error).message)
      if (!(alreadyAnswered && item.attempts > 0)) throw e
    }
    await db.files.bulkDelete(a.fileIds)
  })

  registerOutboxHandler('project.outcome', async (item) => {
    const o = item.payload as Outcome
    await rpc('record_outcome', { pid: o.projectId, stage: o.stage, details: o.details })
  })
}
